import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { effect, inject, Injectable, OnDestroy } from '@angular/core';
import { finalize } from 'rxjs';

import { environment } from '@/environments/environment';
import { AuthService } from './auth.service';
import { SessionTimeoutService } from './session-timeout.service';

/**
 * Sends a lightweight heartbeat only when the user is actively interacting with the UI.
 * This keeps the backend session alive for engaged users without defeating idle timeouts.
 */
@Injectable({ providedIn: 'root' })
export class SessionHeartbeatService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly sessionTimeoutService = inject(SessionTimeoutService);

  private readonly activityEvents = [
    'click',
    'keydown',
    'mousemove',
    'scroll',
    'touchstart',
    'wheel',
  ];
  private readonly activityWindowMs = 2 * 60 * 1000; // consider active if an event happened within the last 2 minutes
  private readonly minHeartbeatIntervalMs = 60 * 1000; // do not send heartbeats more often than once per minute
  private readonly checkIntervalMs = 15 * 1000;

  private heartbeatTimerId: ReturnType<typeof setInterval> | null = null;
  private active = false;
  private inFlight = false;
  private lastActivityAt = 0;
  private lastHeartbeatAt = 0;

  private readonly watcher = effect(() => {
    const isAuthenticated = this.authService.isAuthenticated();
    if (isAuthenticated) {
      this.start();
    } else {
      this.stop();
    }
  });

  private readonly activityListener = () => {
    if (!this.active) {
      return;
    }
    this.lastActivityAt = Date.now();
    this.evaluateHeartbeat();
  };

  private readonly visibilityListener = () => {
    if (!this.active || typeof document === 'undefined') {
      return;
    }
    if (!document.hidden) {
      this.lastActivityAt = Date.now();
      this.evaluateHeartbeat(true);
    }
  };

  private start(): void {
    if (this.active || typeof document === 'undefined') {
      return;
    }
    this.active = true;
    this.lastActivityAt = Date.now();
    this.lastHeartbeatAt = 0;

    this.activityEvents.forEach(event =>
      document.addEventListener(event, this.activityListener, { passive: true }),
    );
    document.addEventListener('visibilitychange', this.visibilityListener, { passive: true });

    // E2X-2035: skip the immediate forced heartbeat — on login it races with token
    // persistence and produces a spurious 401. The interval loop below will fire within
    // 15s once the user interacts, which is fast enough to keep the session warm.
    this.heartbeatTimerId = setInterval(() => this.evaluateHeartbeat(), this.checkIntervalMs);
  }

  private stop(): void {
    if (!this.active) {
      return;
    }
    this.active = false;
    if (this.heartbeatTimerId) {
      clearInterval(this.heartbeatTimerId);
      this.heartbeatTimerId = null;
    }
    if (typeof document !== 'undefined') {
      this.activityEvents.forEach(event =>
        document.removeEventListener(event, this.activityListener),
      );
      document.removeEventListener('visibilitychange', this.visibilityListener);
    }
    this.inFlight = false;
    this.lastActivityAt = 0;
    this.lastHeartbeatAt = 0;
  }

  private evaluateHeartbeat(force = false): void {
    if (!this.active || this.inFlight) {
      return;
    }
    if (!this.authService.isAuthenticated() || typeof document === 'undefined') {
      return;
    }

    const now = Date.now();
    if (!force) {
      if (document.hidden) {
        return;
      }
      if (now - this.lastActivityAt > this.activityWindowMs) {
        return;
      }
      if (now - this.lastHeartbeatAt < this.minHeartbeatIntervalMs) {
        return;
      }
    }

    this.sendHeartbeat();
  }

  private sendHeartbeat(): void {
    this.inFlight = true;
    this.http
      .post(
        `${environment.apiUrl}/v1/auth/session/heartbeat`,
        { ts: Date.now() },
        {
          observe: 'response',
        },
      )
      .pipe(
        finalize(() => {
          this.inFlight = false;
        }),
      )
      .subscribe({
        next: () => {
          this.lastHeartbeatAt = Date.now();
        },
        error: (err: HttpErrorResponse) => {
          // E2X-1691: Handle 401 errors properly instead of swallowing them
          if (err.status === 401) {
            // E2X-1691: Only notify if not already handling expiration (prevents race conditions)
            if (!this.sessionTimeoutService.isHandlingExpiration()) {
              // Session was revoked on server (likely idle timeout)
              this.sessionTimeoutService.notifySessionExpired({
                statusCode: err.status,
                requestUrl: '/v1/auth/session/heartbeat',
                reason: 'heartbeatFailed',
              });
            }
          }
          // Other errors can be safely ignored; next user activity will retry
        },
      });
  }

  ngOnDestroy(): void {
    this.stop();
    this.watcher.destroy();
  }
}
